import { FooterButtons, NullableKeys, Options, superUniqueClassName } from '@fl/cmsch-fe-library';
import moment from 'moment';
import React, { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Opt } from 'ts-opt';
import { UserProfileOtherSubjectRole } from 'api/gen/UserProfileOtherSubjectRole';
import { useOurTranslation } from 'app/translations';
import { AsyncValidationSource } from 'utils/async-validation-source';
import { createAsyncValidationHandler, formHelpers, useForm } from 'utils/forms';
import { Errors } from 'utils/validator';
import { simpleAdminSelector } from '../../model/selector';
import { managementOtherSubjectFormInitialValues, managementOtherSubjectFormName, ManagementOtherSubjectFormValues } from '../../types/management-other-subject-form-values';
import { UserProfileAndRole } from '../../types/user-profile-and-role-ids';
import styles from '../global-management-styles.sass';
interface Props {
  /** Selected user and role pair. */
  userProfileAndRole: UserProfileAndRole;
  /** Selected subject id. */
  subjectId: number;
  /** User selector options. */
  userProfileOptions: Options<number>;
  /** Role selector options. */
  roleOptions: Options<number>;
  /** Whether the form is in edit mode. */
  isEdit: boolean;
  /** Existing binding data when editing. */
  dataForEdit: Opt<UserProfileOtherSubjectRole>;
  /** Called when the form is submitted. */
  onSubmit(): void;
  /** Called when the form is cancelled. */
  onCancel(): void;
  /** Searches users for the select field. */
  onUserSearch(searchText: string): void;
  /** Searches roles for the select field. */
  onRoleSearch(searchText: string): void;
  /** Triggers synchronous validation refresh. */
  onSyncValidate(source: AsyncValidationSource): void;
  /** Validation function for the form. */
  validate(values: ManagementOtherSubjectFormValues): Errors<ManagementOtherSubjectFormValues>;
  /** Clears stored form values. */
  clearFormValues(): void;
}

// eslint-disable-next-line max-lines-per-function
const ManagementOtherSubjectFormBase: FC<Props> = ({
  userProfileAndRole: {
    userProfile,
    role
  },
  userProfileOptions,
  roleOptions,
  subjectId,
  isEdit,
  dataForEdit,
  onSubmit,
  onCancel,
  onUserSearch,
  onRoleSearch,
  onSyncValidate,
  validate,
  clearFormValues
}) => {
  const {
    t,
    tCommon
  } = useOurTranslation('admin');
  const {
    id: userProfileId,
    name: userProfileName
  } = userProfile ?? {
    id: null,
    name: ''
  };
  const {
    id: roleId,
    name: roleName
  } = role ?? {
    id: null,
    name: ''
  };
  const loading = useSelector(simpleAdminSelector.loading);
  const initialValues: NullableKeys<ManagementOtherSubjectFormValues> = useMemo(() => {
    if (isEdit && dataForEdit.isFull) {
      return {
        approvedFrom: dataForEdit.propOrCrash('approvedFrom'),
        approvedTo: dataForEdit.prop('approvedTo').orNull(),
        subjectId,
        roleIds: dataForEdit.prop('role').prop('id').toArray(),
        userProfileId: dataForEdit.prop('user').prop('userProfileId').orNull(),
        userProfileRoleIds: dataForEdit.prop('userProfileOtherSubjectRoleId').toArray(),
        tag: 'ValidateUpdateOtherSubjectBindingManager'
      };
    } else {
      return managementOtherSubjectFormInitialValues(userProfileId, roleId, subjectId);
    }
  }, [dataForEdit, isEdit, subjectId, userProfileId, roleId]);
  const {
    Form,
    Fields,
    submitting,
    valid,
    pristine,
    asyncValidating,
    change
  } = useForm({
    form: managementOtherSubjectFormName,
    initialValues,
    destroyOnUnmount: true,
    onSubmit,
    validate
  });
  const approvedToRef = useRef<HTMLInputElement>(null);
  const approveFrom = useSelector(formHelpers.formValues(managementOtherSubjectFormName)).prop('approvedFrom').orUndef();
  const approveFromMoment = useMemo(() => moment(approveFrom), [approveFrom]);
  const handleApproveFromChange = useCallback((value: Opt<string>) => {
    onSyncValidate('approvedTo');
    value.onSome(date => {
      const [element] = document.getElementsByClassName(superUniqueClassName);
      element?.classList.remove('ant-picker-focused');
      setTimeout(() => approvedToRef.current?.focus());
      change('approvedFrom', date);
    });
  }, [onSyncValidate, approvedToRef, change]);
  useEffect(() => {
    if (!isEdit) {
      onUserSearch(userProfileName);
      onRoleSearch(roleName);
    }
  }, [onUserSearch, onRoleSearch, roleName, userProfileName, isEdit]);
  useEffect(() => () => {
    clearFormValues();
  }, [clearFormValues]);
  return <Form data-sentry-element="Form" data-sentry-component="ManagementOtherSubjectFormBase" data-sentry-source-file="index.tsx">
            <div className={styles.formContainer}>
                <div className={styles.doubleWidth}>
                    <Fields.NumberSelect name="userProfileId" label={t('loginName')} options={userProfileOptions} disabled={submitting || isEdit || Boolean(userProfileId)} onSearch={onUserSearch} onFieldChange={createAsyncValidationHandler(onSyncValidate, 'userProfileId')} fieldIsLoading={loading.includes('getUsers')} asyncSearch isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.NumberMultiSelect name="roleIds" label={t('roles')} options={roleOptions} disabled={submitting || isEdit || Boolean(roleId)} onFieldChange={createAsyncValidationHandler(onSyncValidate, 'roleIds')} onSearch={onRoleSearch} fieldIsLoading={loading.includes('getRoles')} asyncSearch isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div>
                    <Fields.DateInput name="approvedFrom" label={t('approvedFrom')} onFieldChange={handleApproveFromChange} isRequired clearable dontIncludePast disabled={isEdit} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div>
                    <Fields.DateInput name="approvedTo" label={t('approvedTo')} onFieldChange={createAsyncValidationHandler(onSyncValidate, 'approvedFrom')} clearable dontIncludePast inputRef={approvedToRef} minDate={approveFromMoment} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
            </div>
            <FooterButtons submitting={submitting} valid={valid} pristine={pristine} onCancel={onCancel} asyncValidating={asyncValidating} submitTitle={isEdit ? tCommon('save') : tCommon('add')} data-sentry-element="FooterButtons" data-sentry-source-file="index.tsx" />
        </Form>;
};

/**
 * Other-subject management form.
 *
 * @example
 * <ManagementOtherSubjectForm
 *   userProfileAndRole={userProfileAndRole}
 *   subjectId={123}
 *   userProfileOptions={userProfileOptions}
 *   roleOptions={roleOptions}
 *   isEdit={false}
 *   dataForEdit={none}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onUserSearch={handleUserSearch}
 *   onRoleSearch={handleRoleSearch}
 *   onSyncValidate={handleSyncValidate}
 *   validate={validate}
 *   clearFormValues={clearFormValues}
 * />
 */
export const ManagementOtherSubjectForm = memo(ManagementOtherSubjectFormBase);